import * as moment from 'moment';
import { IDonorRegistration } from '@nkr/models/index';
import { IDashWorkFlowStatusRequest } from '@nkr/models/dash-workflow-status-request';
import { TypeOption } from '../../../donor/add/data-model';
import { canShowAdvReimburseAction } from '../../watr/components/watr-nav-area';
import { hasForeignResidence } from '../../watr/components/utils/international-eligibility';
import { DashDonationType } from '@nkr/enums/index';

export const WORKUP_SCHEDULED_REMOTE_STATUS_ID = 10;
export const PRE_OP_SCHEDULED_REMOTE_STATUS_ID = 25;

export interface IAdvancedReimbursementNetworkPayload {
    applyForDonorShieldAdvancedReimbursement?: boolean | null,
    donorAlias?: string | null,
    advancedReimbursementDonorType?: number | null,
    ddRecipFirstName?: string | null,
    ddRecipLastName?: string | null,
    ddRecipDob?: string | null,
    recipientAlias?: string | null,
    linkedDonorShieldDirectId?: number | null,
    linkedDonorId?: number | null
}

export function isEligibleForAdvancedReimbursementInvite(reg: IDonorRegistration | null): boolean {
    if (!reg) {
        return false;
    }
    if (reg.donorShieldAdvancedReimbursementReady === true) {
        return false;
    }
    if (reg.isInvitedToDonorShield === true) {
        return false;
    }
    return canShowAdvReimburseAction(
        reg.centerId,
        reg.donationType,
        reg.countryOfResidenceId,
        reg.isDcnParticipant
    );
}

function isIntlForIntakeInvite(reg: IDonorRegistration | null): boolean {
    return !!reg && hasForeignResidence(reg.countryOfResidenceId);
}

export function isEnrolledInAdvancedReimbursement(reg: IDonorRegistration | null): boolean {
    if (!reg) {
        return false;
    }
    return reg.isInvitedToDonorShield === true || reg.donorShieldAdvancedReimbursementReady === true;
}

export function shouldPromptAdvancedReimbursementEnrolledScheduleModal(reg: IDonorRegistration | null): boolean {
    if (!reg || !isEnrolledInAdvancedReimbursement(reg)) {
        return false;
    }
    return isWorkupScheduledWithFutureDate(reg) || isPreOpScheduledWithFutureDate(reg);
}

function parseScheduledDate(remoteStatusDate: string | null | undefined): moment.Moment | null {
    if (remoteStatusDate == null || String(remoteStatusDate).trim() === '') {
        return null;
    }
    const raw = String(remoteStatusDate).trim();
    let m = moment(raw, 'MM/DD/YYYY', true);
    if (m.isValid()) {
        return m;
    }
    m = moment(raw, 'M/D/YYYY', true);
    if (m.isValid()) {
        return m;
    }
    m = moment(raw, 'YYYY-MM-DD', true);
    if (m.isValid()) {
        return m;
    }
    m = moment(raw);
    return m.isValid() ? m : null;
}

export function formatScheduledDate(remoteStatusDate: string | null | undefined): string | null {
    const parsed = parseScheduledDate(remoteStatusDate);
    return parsed != null ? parsed.format('MM/DD/YYYY') : null;
}

export function isWorkupScheduledWithFutureDate(reg: IDonorRegistration | null): boolean {
    if (!reg || Number(reg.remoteStatus) !== WORKUP_SCHEDULED_REMOTE_STATUS_ID) {
        return false;
    }
    const parsed = parseScheduledDate(reg.remoteStatusDate);
    return parsed != null && parsed.isAfter(moment(), 'day');
}

export function isPreOpScheduledWithFutureDate(reg: IDonorRegistration | null): boolean {
    if (!reg || Number(reg.remoteStatus) !== PRE_OP_SCHEDULED_REMOTE_STATUS_ID) {
        return false;
    }
    const parsed = parseScheduledDate(reg.remoteStatusDate);
    return parsed != null && parsed.isAfter(moment(), 'day');
}

export function advancedReimbursementNetworkPromptSnapshot(reg: IDonorRegistration): string {
    return `${Number(reg.remoteStatus ?? -1)}|${String(reg.remoteStatusDate || '')}`;
}

export function shouldPromptAdvancedReimbursementWorkupModal(reg: IDonorRegistration | null, declinedSnapshot: string | null): boolean {
    return shouldPromptAdvancedReimbursementNetworkModal(reg, declinedSnapshot);
}

export function shouldPromptAdvancedReimbursementNetworkModal(reg: IDonorRegistration | null, declinedSnapshot: string | null): boolean {
    if (!reg || !isEligibleForAdvancedReimbursementInvite(reg)) {
        return false;
    }
    const workup = isWorkupScheduledWithFutureDate(reg);
    const preOp = isPreOpScheduledWithFutureDate(reg);
    if (isIntlForIntakeInvite(reg)) {
        if (!workup) {
            return false;
        }
    } else if (!workup && !preOp) {
        return false;
    }
    const snap = advancedReimbursementNetworkPromptSnapshot(reg);
    if (declinedSnapshot != null && declinedSnapshot === snap) {
        return false;
    }
    return true;
}

export function resolveApplyForDonorShieldAdvancedReimbursementFlag(
    reg: IDonorRegistration,
    declinedSnapshot: string | null,
    inviteExplicit?: 'yes' | 'no'
): boolean | undefined {
    const preOp = isPreOpScheduledWithFutureDate(reg);
    const workup = isWorkupScheduledWithFutureDate(reg);
    if (isIntlForIntakeInvite(reg)) {
        if (!workup) {
            return undefined;
        }
    } else if (!workup && !preOp) {
        return undefined;
    }
    if (!isEligibleForAdvancedReimbursementInvite(reg)) {
        return undefined;
    }
    if (inviteExplicit === 'yes') {
        return true;
    }
    if (inviteExplicit === 'no') {
        return false;
    }
    const snap = advancedReimbursementNetworkPromptSnapshot(reg);
    if (declinedSnapshot != null && declinedSnapshot === snap) {
        return false;
    }
    return true;
}

export function buildAdvancedReimbursementNetworkExtras(
    reg: IDonorRegistration,
    declinedSnapshot: string | null,
    inviteExplicit?: 'yes' | 'no',
    form?: {
        donorAlias: string,
        donorType?: number,
        ddRecipFirstName?: string,
        ddRecipLastName?: string,
        ddRecipDob?: string,
        recipientAlias?: string,
        linkedDonorShieldDirectId?: number | null,
        linkedDonorId?: number | null
    } | null
): IAdvancedReimbursementNetworkPayload {
    const apply = resolveApplyForDonorShieldAdvancedReimbursementFlag(reg, declinedSnapshot, inviteExplicit);
    const extras: IAdvancedReimbursementNetworkPayload = {};
    if (apply !== undefined) {
        extras.applyForDonorShieldAdvancedReimbursement = apply;
    }
    if (inviteExplicit !== 'yes' || !form) {
        return extras;
    }
    const aliasTrim = (form.donorAlias || '').trim();
    if (aliasTrim !== '') {
        extras.donorAlias = aliasTrim;
    }
    const isLiver = Number(reg.donationType) === DashDonationType.Liver;
    if (!isLiver && form.donorType != null) {
        extras.advancedReimbursementDonorType = form.donorType;
    }
    const recipAliasTrim = (form.recipientAlias || '').trim();
    if (recipAliasTrim !== '') {
        extras.recipientAlias = recipAliasTrim;
    }
    if (isLiver) {
        if (form.linkedDonorShieldDirectId != null && form.linkedDonorShieldDirectId > 0) {
            extras.linkedDonorShieldDirectId = form.linkedDonorShieldDirectId;
        }
    } else if (form.linkedDonorId != null && form.linkedDonorId > 0) {
        extras.linkedDonorId = form.linkedDonorId;
    }
    if ((form.donorType ?? TypeOption.Select) !== TypeOption.PairPd && !isLiver) {
        return extras;
    }
    const recipFirst = (form.ddRecipFirstName || '').trim();
    const recipLast = (form.ddRecipLastName || '').trim();
    const recipDob = (form.ddRecipDob || '').trim();
    if (recipFirst !== '') {
        extras.ddRecipFirstName = recipFirst;
    }
    if (recipLast !== '') {
        extras.ddRecipLastName = recipLast;
    }
    if (recipDob !== '') {
        extras.ddRecipDob = recipDob;
    }
    return extras;
}

export function mergeAdvancedReimbursementIntoChangeStatusRequest(
    request: IDashWorkFlowStatusRequest,
    reg: IDonorRegistration,
    declinedSnapshot: string | null,
    inviteExplicit?: 'yes' | 'no',
    form?: {
        donorAlias: string,
        donorType?: number,
        ddRecipFirstName?: string,
        ddRecipLastName?: string,
        ddRecipDob?: string,
        recipientAlias?: string,
        linkedDonorShieldDirectId?: number | null,
        linkedDonorId?: number | null
    } | null
): IDashWorkFlowStatusRequest {
    const extras = buildAdvancedReimbursementNetworkExtras(reg, declinedSnapshot, inviteExplicit, form);
    return {
        ...request,
        applyForDonorShieldAdvancedReimbursement: extras.applyForDonorShieldAdvancedReimbursement,
        donorAlias: extras.donorAlias ?? null,
        advancedReimbursementDonorType: extras.advancedReimbursementDonorType ?? null,
        ddRecipFirstName: extras.ddRecipFirstName ?? null,
        ddRecipLastName: extras.ddRecipLastName ?? null,
        ddRecipDob: extras.ddRecipDob ?? null,
        recipientAlias: extras.recipientAlias ?? null,
        linkedDonorShieldDirectId: extras.linkedDonorShieldDirectId ?? null,
        linkedDonorId: extras.linkedDonorId ?? null
    };
}

export function isAdvancedReimbursementInviteMilestone(reg: IDonorRegistration | null): boolean {
    if (isIntlForIntakeInvite(reg)) {
        return isWorkupScheduledWithFutureDate(reg);
    }
    return isWorkupScheduledWithFutureDate(reg) || isPreOpScheduledWithFutureDate(reg);
}

export function isAdvancedReimbursementInviteFlowApplicable(
    reg: IDonorRegistration | null,
    applyForDonorShieldAdvancedReimbursement: boolean | null | undefined
): boolean {
    if (!isAdvancedReimbursementInviteMilestone(reg)) {
        return false;
    }
    return applyForDonorShieldAdvancedReimbursement !== false;
}
